import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SiteNav from './SiteNav';
import Footer from './Footer';
import aboutPhoto from './assets-optimized/about-piotr.webp';
import aboutPhotoMask from './assets-optimized/about-piotr-mask.webp';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';
import './App.css';

// Only the DESKTOP hero layout ties bio's position/width to POSITIVE's
// real rendered box - in the mobile stacked layout, .about-positive
// becomes a static block and bio should just follow the normal, simple
// mobile flow (padding-based, full width) instead of being pinned to a
// measured rect. This mirrors the same breakpoint used in the CSS.
const MOBILE_QUERY = '(max-width: 980px), (orientation: portrait)';

export default function About() {
  const { lang } = useLanguage();
  const t = translations[lang].about;

  const positiveRef = useRef<HTMLDivElement>(null);
  const [bioBox, setBioBox] = useState<{ left: number; width: number } | null>(null);

  // Measures POSITIVE's ACTUAL rendered position/width (not a guessed
  // CSS value - the word's real pixel box, whatever the browser/font
  // renders it as) and copies it onto bio's container. This is the only
  // way to guarantee "bio starts exactly where POSITIVE starts, and is
  // exactly as wide" - the two are unrelated CSS elements otherwise, and
  // POSITIVE's width isn't something plain CSS can compute (it depends
  // on font metrics/kerning at whatever size cqw resolves to). Bio's
  // width matches POSITIVE's rect.width exactly - a wide-screen stretch
  // was tried and dropped after you flagged it overshooting POSITIVE's
  // right edge; bio's own additional "breathing room" on wide screens
  // now comes only from POSITIVE moving further from the photo
  // (--about-wide-extra-gap in App.css), not from bio being wider than
  // POSITIVE itself.
  useLayoutEffect(() => {
    const measure = () => {
      const mobile = window.matchMedia(MOBILE_QUERY).matches;
      if (mobile || !positiveRef.current) {
        setBioBox(null); // let the mobile CSS rules take over untouched
        return;
      }
      const rect = positiveRef.current.getBoundingClientRect();
      setBioBox({ left: rect.left, width: rect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Re-measure once the real font has finished loading - before that,
  // the browser may render POSITIVE in a fallback serif with different
  // metrics, giving a slightly wrong width that would otherwise stick
  // until the next resize.
  useEffect(() => {
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled || !positiveRef.current) return;
      const mobile = window.matchMedia(MOBILE_QUERY).matches;
      if (mobile) return;
      const rect = positiveRef.current.getBoundingClientRect();
      setBioBox({ left: rect.left, width: rect.width });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="simple-page about-page">
      <SiteNav />

      {/* Mobile-only fixed header backdrop - a SECOND instance of the
          same photo (aboutPhoto), full-height and same object-fit/
          object-position as the real one in .about-hero, so it computes
          the IDENTICAL crop/scale - the wrapper then clips that to just
          the header's height via overflow:hidden. Since it's the same
          image rendered with the same math, just visually clipped to a
          thin strip, the seam against the real (scrolling) photo below
          is invisible at scroll position 0. Hidden entirely on desktop
          via CSS (display:none there) - .about-hero stays fixed with
          plenty of header clearance built into POSITIVE's own top
          offset on desktop, so this extra element isn't needed there. */}
      <div className="about-mobile-header-backdrop" aria-hidden="true">
        <img className="about-mobile-header-backdrop-img" src={aboutPhoto} alt="" />
      </div>

      {/* .about-hero is the only position:fixed element in the whole
          composition - POSITIVE, the photo box, GRAIN and the mask all
          live inside it as position:absolute, sharing one cqw/cqh scale
          (see App.css for the full reasoning). */}
      <div className="about-hero fade-in-up">
        <div className="about-positive" ref={positiveRef} aria-hidden="true">
          Positive
        </div>

        <div className="about-photo-composition fade-in-up-delay">
          <img className="about-photo-base" src={aboutPhoto} alt="Piotr Szkałuba" />

          <div className="about-grain" aria-hidden="true">
            Grain
          </div>

          <img
            className="about-photo-foreground"
            src={aboutPhotoMask}
            alt=""
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Bio - deliberately outside .about-hero's own coordinate system
          (see App.css), but pinned to POSITIVE's MEASURED left/width via
          inline style once available. Falls back to the CSS defaults
          below until the first measurement lands, and again whenever the
          mobile layout is active (inline style is simply omitted then). */}
      <main
        className="about-page-content"
        style={bioBox ? { left: bioBox.left, width: bioBox.width } : undefined}
      >
        <div className="about-text">
          <div className="about-bio-flow">
            <p className="about-bio">{t.p1}</p>
            <p className="about-bio">{t.p2}</p>
            <p className="about-bio">{t.p3}</p>
            <p className="about-bio">{t.p4}</p>
            <p className="about-bio">{t.p5}</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}